import { FC, useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import ReactPlayer from "react-player";
import { getPureArray } from "utils/util";
import Heading2 from "components/Heading/Heading2";
import { commonAxiosPostRequest } from "utils/request";
import { getYouTubePlaylistEmbedUrl } from "../youtubePlaylist";
import "./index.css";

interface IContentInfo {
	videoUrl?: string;
	linkUrl?: string;
	pictureUrl?: string;
	onVideoError?: () => void;
}

function isIgnorablePlayerError(error: unknown, data?: unknown) {
	if (data && typeof data === "object" && "fatal" in data && (data as { fatal?: boolean }).fatal === false) {
		return true;
	}
	if (error && typeof error === "object" && "name" in error) {
		const name = (error as { name?: string }).name;
		return name === "NotAllowedError" || name === "AbortError";
	}
	return false;
}

function ContentSelector(props: IContentInfo) {
	const { videoUrl, pictureUrl, linkUrl, onVideoError } = props;
	const [videoFailed, setVideoFailed] = useState(false);
	const onVideoErrorRef = useRef(onVideoError);
	const unsupported = Boolean(videoUrl) && !ReactPlayer.canPlay(videoUrl as string);

	onVideoErrorRef.current = onVideoError;

	useEffect(() => {
		if (unsupported) {
			onVideoErrorRef.current?.();
		}
	}, [unsupported]);

	if (videoFailed || unsupported) {
		return null;
	}

	const playlistEmbedUrl = getYouTubePlaylistEmbedUrl(videoUrl);
	if (videoUrl && playlistEmbedUrl) {
		return (
			<div className="w-full">
				<div className="w-full h-[315px]">
					<iframe
						title="YouTube playlist"
						src={playlistEmbedUrl}
						className="w-full h-full"
						allow="autoplay; encrypted-media; picture-in-picture"
						allowFullScreen
					/>
				</div>
			</div>
		);
	}

	if (videoUrl) {
		return (
			<div className="w-full">
				<div className="w-full h-[315px]">
					<ReactPlayer
						controls
						width="100%"
						height="100%"
						url={videoUrl}
						muted={true}
						playing={true}
						onError={(error, data) => {
							if (isIgnorablePlayerError(error, data)) {
								return;
							}
							setVideoFailed(true);
							onVideoError?.();
						}}
					/>
				</div>
			</div>
		);
	}

	return (
		<div className="w-full">
			<a href={linkUrl} className="w-full h-full">
				<img src={pictureUrl || ''} alt="" className="w-full h-full rounded-[12px]" />
			</a>
		</div>
	);
}

interface CarouselProps {
	heading?: string;
	leftIsImage?: boolean;
};

const PromotionBlock: FC<CarouselProps> = (props) => {
	const { t } = useTranslation();
	const [bannerList, setBannerList] = useState<any[]>([]);
	const [failedKeys, setFailedKeys] = useState<string[]>([]);

	const markVideoFailed = useCallback((key: string) => {
		setFailedKeys((prev) => (prev.includes(key) ? prev : [...prev, key]));
	}, []);

	const handleQueryBannerList = async () => {
		const [error, response] = await commonAxiosPostRequest<{ list: any[] }, {}>('/universal_mall/api/app/common/discountList', {});
		const list = getPureArray<any>(response?.list);
		setBannerList(list);
		// setBannerList([
		// 	{ pictureUrl: 'http://ywca-oss.oss-cn-hongkong.aliyuncs.com/ywca/admin/2024/8/5a723e405b8111ef885c5d9f5d440c27.jpg' },
		// 	{ pictureUrl: 'http://ywca-oss.oss-cn-hongkong.aliyuncs.com/ywca/admin/2024/8/66c0e0c05b8111ef885c5d9f5d440c27.jpg' },
		// ]);
	}

	useEffect(() => {
		handleQueryBannerList();
	}, []);

	const visibleList = bannerList
		.map((item, index) => ({ item, key: String(item.id ?? index) }))
		.filter(({ key }) => !failedKeys.includes(key));

	if (!visibleList.length) {
		return null;
	}

	return (
		<section className="mt-8 sm:mt-8 lg:mt-16">
			{props.heading ? <Heading2 heading={t('product.promotions')} className="mb-8 mb-0" /> : null}
			<div className="container">
				<div className="grid sm:grid-cols-1 md:grid-cols-2 gap-4">
					{
						visibleList.map(({ item, key }) => (
							<ContentSelector
								key={key}
								videoUrl={item.videoUrl}
								pictureUrl={item.pictureUrl}
								linkUrl={item.linkUrl}
								onVideoError={() => markVideoFailed(key)}
							/>
						))
					}
				</div>
			</div>
		</section>
	);
}


export default PromotionBlock;